// public/v3/src/lib/suggest.js — Quick-log suggestion engine.
//
// Replaces the old static dx -> preset map (`SUGGEST_REASONS`) with rule-based
// heuristics that read what's actually in the patient's chart this CCM period.
//
// Two entry points:
//
//   suggestForPatientSync(patient, presets, opts)
//     Synchronous. Uses only data already loaded on the patient blob
//     (medications, outsideProviders, activityLog, conditions). Cheap enough
//     to call per row on every render. Returns up to 3 suggestions.
//
//   suggestForPatientWithContext(patient, presets, context, opts)
//     Same heuristics + an extra `context` argument carrying labs and the
//     latest visit-note text. The context shape is:
//       { labReports: [...], latestVisitNote: { text: '...', date: 'YYYY-MM-DD' } | null }
//     Used by the AI scan & suggest modal after it's prefetched per-patient
//     data in parallel.
//
// Both return an array of `{ preset, why }` items, ordered by priority:
//   Lab review > Med PA > Care coord > Caregiver > Services > Plan update
// De-duped by preset.id; capped at 3 items. The fallback "Plan update" is
// always available so a below-target patient never shows zero suggestions.

import { isContributorEntry } from './activity-log';

// Preset labels we look up by, so the engine doesn't depend on array indices.
// If a practice's edited preset list is missing one of these labels, we
// silently drop the matching suggestion rather than picking the wrong row.
const LABEL = {
  CARE_COORD:  'Care coord',
  CAREGIVER:   'Caregiver',
  SERVICES:    'Services',
  MED_PA:      'Med PA',
  LAB_REVIEW:  'Lab review',
  PLAN_UPDATE: 'Plan update',
};

// Priority order — highest priority first. Used to sort and to cap at 3.
const PRIORITY = [
  LABEL.LAB_REVIEW,
  LABEL.MED_PA,
  LABEL.CARE_COORD,
  LABEL.CAREGIVER,
  LABEL.SERVICES,
  LABEL.PLAN_UPDATE,
];

function findPreset(presets, label) {
  return (presets || []).find(p => p && p.label === label) || null;
}

// YYYY-MM derived from a Date (in local TZ). The activityLog filter and the
// "this month" lab filter both use the active month key — opts.monthKey wins
// when provided so callers can scan a specific period (the page already
// tracks the active month).
function currentMonthKey(opts) {
  if (opts && opts.monthKey) return String(opts.monthKey);
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Loose date parser — labs come back with `report_date` ('YYYY-MM-DD') and
// activity entries use `date` ('YYYY-MM-DD'). We only need the first 7 chars
// to compare against a month key, so be forgiving about the rest.
function monthKeyOf(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 7);
  if (value instanceof Date && !isNaN(value)) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
  }
  return '';
}

// ──────────────────────────────────────────────────────────────────────────
// Signal detectors — each returns null OR { label, why }. The actual preset
// object is looked up later via findPreset(). Splitting detection from preset
// lookup means a practice can rename "Lab review" without breaking the rule.
// ──────────────────────────────────────────────────────────────────────────

// Lab review — based on labReports passed in the context. Synchronous variant
// has no labReports and will skip this signal entirely.
function detectLabReview(patient, context, monthKey) {
  const reports = (context && Array.isArray(context.labReports)) ? context.labReports : null;
  if (!reports || !reports.length) return null;
  const thisMonth = reports.filter(r => monthKeyOf(r && r.report_date) === monthKey);
  if (!thisMonth.length) return null;
  const n = thisMonth.length;
  return { label: LABEL.LAB_REVIEW, why: `${n} new lab result${n === 1 ? '' : 's'} this month` };
}

// Med PA — looks for any medication object with a "pending PA" marker.
// We accept several spellings since the data model is loose and a few legacy
// imports use snake_case. The medication name (first one matched) is included
// in the "why" string for context, truncated so the chip tooltip stays terse.
function detectMedPA(patient) {
  const meds = (patient && patient._raw && Array.isArray(patient._raw.medications))
    ? patient._raw.medications
    : [];
  for (const m of meds) {
    if (!m || typeof m !== 'object') continue;
    const pa = String(
      m.priorAuth || m.prior_auth || m.pa_status || m.paStatus || m.pa || ''
    ).toLowerCase().trim();
    if (pa === 'pending' || pa === 'requested' || pa === 'in_progress' || pa === 'submitted') {
      const name = String(m.name || m.medication || m.rx || '').trim();
      const why = name ? `PA pending: ${name.slice(0, 24)}` : 'Pending PA on file';
      return { label: LABEL.MED_PA, why };
    }
  }
  return null;
}

// Specialist coordination — fires when there are outside providers on file,
// OR the latest visit note mentions a referral/specialist/consult. The name
// (or "referral mentioned") seeds a terse "why" string. We prefer the
// outsideProviders signal because it's structured.
function detectCareCoord(patient, context) {
  const op = (patient && patient._raw && Array.isArray(patient._raw.outsideProviders))
    ? patient._raw.outsideProviders
    : [];
  if (op.length) {
    // Use the first provider with at least a name or specialty.
    const first = op.find(p => p && (p.name || p.specialty)) || op[0];
    const label = (first && (first.name || first.specialty)) ? String(first.name || first.specialty) : '';
    const why = label
      ? `Specialist coord — ${label.slice(0, 22)}`
      : 'Specialist coord on file';
    return { label: LABEL.CARE_COORD, why };
  }
  // Fall back to visit-note keyword scan when async context is available.
  const text = (context && context.latestVisitNote && context.latestVisitNote.text)
    ? String(context.latestVisitNote.text).toLowerCase()
    : '';
  if (text) {
    if (/\b(specialist|referral|consult(ed|ation)?)\b/.test(text)) {
      return { label: LABEL.CARE_COORD, why: 'Specialist coord — referral mentioned' };
    }
  }
  return null;
}

// Caregiver — fires when this patient has NO caregiver/family entry logged
// this month. We treat any activityLog entry whose preset label, description,
// or kind contains "caregiver" or "family" as already covered. Patients with
// no activity at all also surface (since they trivially have no caregiver
// entry yet).
function detectCaregiver(patient, monthKey) {
  const log = (patient && patient._raw && Array.isArray(patient._raw.activityLog))
    ? patient._raw.activityLog
    : [];
  // Skip consolidated synthetic entries — their notes string contains every
  // contributor's text, which would falsely satisfy keyword detections (e.g.
  // a deleted caregiver entry would still appear inside the consolidated note).
  const entriesThisMonth = log.filter(e => isContributorEntry(e) && monthKeyOf(e.date) === monthKey);
  const hasCaregiverEntry = entriesThisMonth.some(e => {
    const blob = `${e.label || ''} ${e.desc || ''} ${e.kind || ''} ${e.type || ''}`.toLowerCase();
    return /caregiver|family/.test(blob);
  });
  if (hasCaregiverEntry) return null;
  return { label: LABEL.CAREGIVER, why: 'No caregiver check yet this month' };
}

// Community services — fires when outsideProviders mentions home health,
// visiting nurse, hospice, or similar community-service patterns. Matched
// against the provider name AND specialty so a "Specialty: Home Health"
// entry catches even if the agency name is generic.
function detectServices(patient) {
  const op = (patient && patient._raw && Array.isArray(patient._raw.outsideProviders))
    ? patient._raw.outsideProviders
    : [];
  if (!op.length) return null;
  const re = /\b(home\s*health|visiting\s*nurse|hospice|meals on wheels|community\s*service|pt\s*at\s*home|home\s*pt)\b/i;
  for (const p of op) {
    if (!p) continue;
    const blob = `${p.name || ''} ${p.specialty || ''}`;
    if (re.test(blob)) {
      return { label: LABEL.SERVICES, why: 'Community services on file' };
    }
  }
  return null;
}

// Plan update — always a fallback so we always have at least one suggestion.
function detectPlanUpdate(/* patient */) {
  return { label: LABEL.PLAN_UPDATE, why: 'Monthly plan-of-care review due' };
}

// ──────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────

// Build the candidate list, resolve presets, de-dupe by preset.id, cap at 3.
function buildSuggestions(patient, presets, context, opts) {
  const monthKey = currentMonthKey(opts);
  const candidates = [];
  // Order of pushes doesn't matter — we sort by PRIORITY before slicing.
  const lab = detectLabReview(patient, context, monthKey);
  if (lab) candidates.push(lab);
  const med = detectMedPA(patient);
  if (med) candidates.push(med);
  const care = detectCareCoord(patient, context);
  if (care) candidates.push(care);
  const cg = detectCaregiver(patient, monthKey);
  if (cg) candidates.push(cg);
  const svc = detectServices(patient);
  if (svc) candidates.push(svc);
  candidates.push(detectPlanUpdate());

  // Sort by priority. Same-priority entries keep their push order (which
  // doesn't currently matter — no two detectors share a label).
  candidates.sort((a, b) => PRIORITY.indexOf(a.label) - PRIORITY.indexOf(b.label));

  // Resolve label -> preset, de-dupe, cap at 3.
  const seen = new Set();
  const out = [];
  for (const c of candidates) {
    const preset = findPreset(presets, c.label);
    if (!preset) continue; // Practice may have renamed/removed the preset.
    if (seen.has(preset.id)) continue;
    seen.add(preset.id);
    out.push({ preset, why: c.why });
    if (out.length >= 3) break;
  }
  return out;
}

// Synchronous variant — no labs, no visit notes. Used per row in the
// PatientChips render path.
export function suggestForPatientSync(patient, presets, opts) {
  return buildSuggestions(patient, presets, null, opts);
}

// Async-context variant — pass in prefetched labs + latest visit note.
// Used by the AI scan & suggest modal after it's done a parallel data scan
// across all below-target patients.
export function suggestForPatientWithContext(patient, presets, context, opts) {
  return buildSuggestions(patient, presets, context || null, opts);
}

// Studio uses a single best suggestion for its inline +Nm chip; this wrapper
// preserves the prior shape `{ preset, why, need } | null` for that call site.
export function bestSuggestionForRoster(patient, presets, currentLogged, opts) {
  const list = suggestForPatientSync(patient, presets, opts);
  if (!list.length) return null;
  const top = list[0];
  const need = Math.max(0, (patient && patient.target ? patient.target : 0) - (currentLogged || 0));
  return { preset: top.preset, why: top.why, need };
}

// ──────────────────────────────────────────────────────────────────────────
// Custom AI chips — patient-data-specific quick-log buttons.
//
// Unlike preset suggestions (which just pick a matching preset to highlight),
// AI chips carry custom labels and descriptions derived from the chart:
//
//   • "PA: Ozempic"          (label) → desc: "Prior auth follow-up — Ozempic"
//   • "Coord: Dr. Singh"     (label) → desc: "Specialist coordination — Dr. Singh"
//   • "Services: home health"(label) → desc: "Community services coordination"
//
// Mins + presetId are inherited from a category preset (so admins still set
// the time amount per category). Click → logs an entry with the custom desc
// and kind:'ai'.
//
// Returns up to 3 chips, ordered by clinical priority (PA > coord > services).
// Synchronous: reads only the patient blob (medications, outsideProviders).

function findPaMed(patient) {
  const meds = (patient && patient._raw && Array.isArray(patient._raw.medications))
    ? patient._raw.medications
    : [];
  for (const m of meds) {
    if (!m || typeof m !== 'object') continue;
    const pa = String(
      m.priorAuth || m.prior_auth || m.pa_status || m.paStatus || m.pa || ''
    ).toLowerCase().trim();
    if (pa === 'pending' || pa === 'requested' || pa === 'in_progress' || pa === 'submitted') {
      const name = String(m.name || m.medication || m.rx || '').trim();
      return { name };
    }
  }
  return null;
}

function findOutsideProvider(patient) {
  const op = (patient && patient._raw && Array.isArray(patient._raw.outsideProviders))
    ? patient._raw.outsideProviders
    : [];
  if (!op.length) return null;
  const first = op.find(p => p && (p.name || p.specialty)) || op[0];
  const name = first && (first.name || first.specialty) ? String(first.name || first.specialty) : '';
  return name ? { name } : null;
}

function findCommunityService(patient) {
  const op = (patient && patient._raw && Array.isArray(patient._raw.outsideProviders))
    ? patient._raw.outsideProviders
    : [];
  const re = /\b(home\s*health|visiting\s*nurse|hospice|meals on wheels|community\s*service|home\s*pt)\b/i;
  for (const p of op) {
    if (!p) continue;
    const blob = `${p.name || ''} ${p.specialty || ''}`;
    const m = blob.match(re);
    if (m) return { tag: m[0].toLowerCase() };
  }
  return null;
}

// Scan phone-note text (already filtered to the active CCM month by the
// server) for keywords that map to specific CCM preset categories. Each
// detector returns a `why` string referencing the note's date when it fires,
// so the chip tooltip points the clinician at the chart evidence.
const PHONE_NOTE_PATTERNS = {
  [LABEL.CAREGIVER]: /\b(family|caregiver|spouse|daughter|son|wife|husband|guardian)\b/i,
  [LABEL.CARE_COORD]: /\b(specialist|referral|consult(ed|ation)?|coordinat\w*)\b/i,
  [LABEL.LAB_REVIEW]: /\b(lab|labs|result(s)?|hba1c|a1c|glucose|panel|imaging|radiology)\b/i,
  [LABEL.MED_PA]: /\b(prior\s+auth(orization)?|pa\s+pending|formulary|pharmacy|refill)\b/i,
  [LABEL.SERVICES]: /\b(home\s*health|visiting\s*nurse|hospice|community|meals on wheels|services?)\b/i,
};

// Returns map: label -> { matched: true, why } when any phone note matches.
// phoneNotes shape: [{ date: 'YYYY-MM-DD', text: '...' }, ...]
function scanPhoneNotes(phoneNotes) {
  const out = {};
  if (!Array.isArray(phoneNotes) || !phoneNotes.length) return out;
  for (const label of Object.keys(PHONE_NOTE_PATTERNS)) {
    const re = PHONE_NOTE_PATTERNS[label];
    for (const n of phoneNotes) {
      if (!n || !n.text) continue;
      if (re.test(n.text)) {
        const d = n.date ? ` (${n.date})` : '';
        out[label] = { matched: true, why: `Phone note${d} mentions ${label.toLowerCase()}` };
        break;
      }
    }
  }
  return out;
}

// Per-preset applicability — gives the UI a way to mark user-inline preset
// chips that probably don't apply to a given patient this month, so a
// clinician sees a visual "are you sure?" before logging time against an
// activity the chart shows no evidence of (e.g., "Lab review" with no labs
// this month, "Med PA" with no pending PAs).
//
// labContext (optional): { hasLabsThisMonth: boolean, labCount: number }
//   — required to flag the Lab review preset; without it Lab review is left
//   un-marked (no data available, no opinion).
//
// Returns Map<presetId, { state: 'applies' | 'unlikely', why }>. Presets not
// in the map have no opinion (e.g., Caregiver, Plan update — always reasonable
// to log).
export function presetApplicability(patient, presets, labContext, opts) {
  const map = new Map();
  const phoneNoteHits = scanPhoneNotes(opts && opts.phoneNotes);

  // Med PA — applies if any med has a pending PA OR a phone note mentions
  // prior auth / refill; unlikely if meds are on file with no signals.
  const medPreset = findPreset(presets, LABEL.MED_PA);
  if (medPreset) {
    const pa = findPaMed(patient);
    if (pa) {
      map.set(medPreset.id, {
        state: 'applies',
        why: pa.name ? `PA pending: ${pa.name}` : 'PA pending on file',
      });
    } else if (phoneNoteHits[LABEL.MED_PA]) {
      map.set(medPreset.id, { state: 'applies', why: phoneNoteHits[LABEL.MED_PA].why });
    } else {
      const meds = (patient && patient._raw && Array.isArray(patient._raw.medications))
        ? patient._raw.medications
        : [];
      map.set(medPreset.id, {
        state: 'unlikely',
        why: meds.length ? 'No pending PA on file' : 'No medications on file',
      });
    }
  }

  // Care coord — applies when outsideProviders exist OR a phone note mentions
  // referral/specialist/coordination. Unlikely otherwise.
  const careCoordPreset = findPreset(presets, LABEL.CARE_COORD);
  if (careCoordPreset) {
    const op = findOutsideProvider(patient);
    if (op) {
      map.set(careCoordPreset.id, { state: 'applies', why: `Specialist on file: ${op.name}` });
    } else if (phoneNoteHits[LABEL.CARE_COORD]) {
      map.set(careCoordPreset.id, { state: 'applies', why: phoneNoteHits[LABEL.CARE_COORD].why });
    } else {
      map.set(careCoordPreset.id, { state: 'unlikely', why: 'No outside providers on file' });
    }
  }

  // Services — applies when a community service is matched in
  // outsideProviders OR a phone note mentions home health/services.
  const svcPreset = findPreset(presets, LABEL.SERVICES);
  if (svcPreset) {
    const svc = findCommunityService(patient);
    if (svc) {
      map.set(svcPreset.id, { state: 'applies', why: `Community service: ${svc.tag}` });
    } else if (phoneNoteHits[LABEL.SERVICES]) {
      map.set(svcPreset.id, { state: 'applies', why: phoneNoteHits[LABEL.SERVICES].why });
    } else {
      map.set(svcPreset.id, { state: 'unlikely', why: 'No community services on file' });
    }
  }

  // Caregiver — phone-note-driven. A note mentioning family/caregiver this
  // month is the only positive signal we have; absence of activityLog
  // caregiver entries is the negative signal.
  const cgPreset = findPreset(presets, LABEL.CAREGIVER);
  if (cgPreset) {
    if (phoneNoteHits[LABEL.CAREGIVER]) {
      map.set(cgPreset.id, { state: 'applies', why: phoneNoteHits[LABEL.CAREGIVER].why });
    }
    // No "unlikely" state for caregiver — absence of evidence isn't evidence
    // of absence (a caregiver may exist without ever appearing in notes).
  }

  // Lab review — labContext OR phone-note keyword match.
  const labPreset = findPreset(presets, LABEL.LAB_REVIEW);
  if (labPreset) {
    if (labContext && labContext.hasLabsThisMonth) {
      const n = labContext.labCount || 0;
      map.set(labPreset.id, {
        state: 'applies',
        why: `${n} new lab result${n === 1 ? '' : 's'} this month`,
      });
    } else if (phoneNoteHits[LABEL.LAB_REVIEW]) {
      map.set(labPreset.id, { state: 'applies', why: phoneNoteHits[LABEL.LAB_REVIEW].why });
    } else if (labContext) {
      map.set(labPreset.id, { state: 'unlikely', why: 'No labs this month' });
    }
  }

  return map;
}

// Only emit a chip when the detector returned a specific named entity (med
// name, provider name, lab count > 0). Without that grounding, fall back to
// the applicability marker on the user's preset chips — don't fabricate a
// generic "PA review" custom chip that adds noise without info.
export function aiChipsForPatient(patient, presets, opts) {
  const out = [];
  const labContext = opts && opts.labContext ? opts.labContext : null;

  // PA pending — only if we have a named medication.
  const pa = findPaMed(patient);
  if (pa && pa.name) {
    const preset = findPreset(presets, LABEL.MED_PA);
    if (preset) {
      out.push({
        id: `ai-pa-${preset.id}`,
        mins: preset.mins,
        label: `PA: ${pa.name.slice(0, 16)}`,
        desc: `Prior auth follow-up — ${pa.name}`,
        why: `PA pending: ${pa.name}`,
        kind: 'ai',
        presetId: preset.id,
      });
    }
  }

  // Specialist coordination — findOutsideProvider already requires a name.
  const op = findOutsideProvider(patient);
  if (op && op.name) {
    const preset = findPreset(presets, LABEL.CARE_COORD);
    if (preset) {
      out.push({
        id: `ai-coord-${preset.id}`,
        mins: preset.mins,
        label: `Coord: ${op.name.slice(0, 16)}`,
        desc: `Specialist coordination — ${op.name}`,
        why: `Specialist on file: ${op.name}`,
        kind: 'ai',
        presetId: preset.id,
      });
    }
  }

  // Community services — surfaced when a known service pattern is matched.
  const svc = findCommunityService(patient);
  if (svc && svc.tag) {
    const preset = findPreset(presets, LABEL.SERVICES);
    if (preset) {
      out.push({
        id: `ai-svc-${preset.id}`,
        mins: preset.mins,
        label: `Services: ${svc.tag.slice(0, 14)}`,
        desc: 'Community services coordination',
        why: `Community service on file: ${svc.tag}`,
        kind: 'ai',
        presetId: preset.id,
      });
    }
  }

  // Lab review — requires labContext from caller's bulk fetch + count > 0.
  if (labContext && labContext.hasLabsThisMonth && labContext.labCount > 0) {
    const preset = findPreset(presets, LABEL.LAB_REVIEW);
    if (preset) {
      const n = labContext.labCount;
      out.push({
        id: `ai-labs-${preset.id}`,
        mins: preset.mins,
        label: `Review labs (${n})`,
        desc: `Review ${n} new lab result${n === 1 ? '' : 's'} this month`,
        why: `${n} new lab result${n === 1 ? '' : 's'} this month`,
        kind: 'ai',
        presetId: preset.id,
      });
    }
  }

  return out.slice(0, 3);
}
